import { useState, useMemo } from 'react';

import { toast } from 'react-toastify';

import {
    type TotemClosedReceptionDayFragmentFragment,
    useGetAllRegularTotemClosedReceptionDayQuery,
    useArchiveTotemClosedReceptionDayMutation,
} from 'data/__generated__';
import { DataTable } from 'components/DataTable';
import { Loader } from 'components/Loader';
import { confirmPopup } from 'components/Popups/Confirm';
import { formatDateAsAnniversary } from 'helpers/dateTimes';

import { totemClosedReceptionDayRemovedHandler } from '../cacheHandlers/totemClosedReceptionDayCacheHandler';
import { AbortTotemClosedReceptionDayPopup } from './AbortTotemClosedReceptionDayPopup';
import {
    getTotemClosedReceptionDaysColumns,
    type TotemClosedReceptionDayRow,
} from '../constants/totemClosedReceptionDaysColumns';

export const TotemClosedReceptionDaysList = () => {
    const [isAbortPopupOpen, setIsAbortPopupOpen] = useState<boolean>(false);
    const [totemClosedReceptionDayToAbort, setTotemClosedReceptionDayToAbort] =
        useState<TotemClosedReceptionDayFragmentFragment | null>(null);

    const {
        loading: totemClosedReceptionDaysLoading,
        data: totemClosedReceptionDaysData,
        error: totemClosedReceptionDaysError,
    } = useGetAllRegularTotemClosedReceptionDayQuery();

    const [archiveTotemClosedReceptionDay, { loading: archiveLoading }] = useArchiveTotemClosedReceptionDayMutation();

    const todayDateFormatted = formatDateAsAnniversary({ dateTime: new Date(), useNewFormat: true });

    async function archive(totemClosedReceptionDayId: string) {
        if (
            await confirmPopup({
                title: 'Confirmer',
                message: 'Voulez-vous archiver ce jour de fermeture ?',
            })
        ) {
            const { data } = await archiveTotemClosedReceptionDay({ variables: { totemClosedReceptionDayId } });

            if (!data) {
                throw new Error('Une erreur est survenue lors de la création du jour de fermeture');
            }

            const { archiveTotemClosedReceptionDay: archivedTotemClosedReceptionDay } = data;

            if (archivedTotemClosedReceptionDay) {
                totemClosedReceptionDayRemovedHandler(archivedTotemClosedReceptionDay._id);
                toast.success('Jour de fermeture archivé');
            } else {
                toast.error('Une erreur est survenue');
            }
        }
    }

    const columns = useMemo(
        () =>
            getTotemClosedReceptionDaysColumns({
                todayDateFormatted,
                archiveLoading,
                onAbort: (totemClosedReceptionDay) => {
                    setTotemClosedReceptionDayToAbort(totemClosedReceptionDay);
                    setIsAbortPopupOpen(true);
                },
                onArchive: archive,
            }),
        [todayDateFormatted, archiveLoading],
    );

    if (totemClosedReceptionDaysLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (!totemClosedReceptionDaysData || totemClosedReceptionDaysError) {
        throw new Error('Une erreur est survenue lors de la récupération des jours de fermeture');
    }

    const { getAllRegularTotemClosedReceptionDay: totemClosedReceptionDays } = totemClosedReceptionDaysData;

    return (
        <>
            <div className="flex h-full w-full flex-col overflow-hidden">
                <DataTable<TotemClosedReceptionDayRow>
                    columns={columns}
                    data={totemClosedReceptionDays}
                    filterMode="client"
                    getRowId={(row) => row._id}
                    searchPlaceholder="Rechercher un jour de fermeture"
                    searchFilterKey="searchValue"
                />
            </div>
            {totemClosedReceptionDayToAbort ? (
                <AbortTotemClosedReceptionDayPopup
                    totemClosedReceptionDay={totemClosedReceptionDayToAbort}
                    isOpen={isAbortPopupOpen}
                    setIsOpen={setIsAbortPopupOpen}
                />
            ) : null}
        </>
    );
};
